import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PenLineIcon, XIcon, CheckedIcon2 } from "@/components/icons";
import { cn } from "@/lib/utils";

type EditableStringListProps = {
  value?: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
};

export const EditableStringList = ({ value = [], onChange, placeholder }: EditableStringListProps) => {
  const [input, setInput] = useState("");
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");

  const add = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    onChange([...value, trimmed]);
    setInput("");
  };

  const toggleItemSelection = (i: number) => {
    if (editingIndex !== null) return;
    setSelectedIndex(i === selectedIndex ? null : i);
  };

  const startEdit = (i: number) => {
    setEditingIndex(i);
    setEditingValue(value[i]);
    setSelectedIndex(null);
  };

  const confirmEdit = () => {
    if (editingIndex === null) return;
    const trimmed = editingValue.trim();
    if (!trimmed) return;
    const updated = [...value];
    updated[editingIndex] = trimmed;
    onChange(updated);
    setEditingIndex(null);
  };

  const cancelEdit = () => setEditingIndex(null);

  const removeItem = (i: number) => {
    onChange(value.filter((_, j) => j !== i));
    setSelectedIndex(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          className="flex-1"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" variant="outline" onClick={add}>
          Ajouter
        </Button>
      </div>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((item, i) =>
            editingIndex === i ? (
              <div
                key={i}
                className="flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 pl-3 pr-1.5 py-1"
              >
                <Input
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className="h-5 w-28 border-0 bg-transparent p-0 text-xs shadow-none focus-visible:ring-0"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      confirmEdit();
                    }
                    if (e.key === "Escape") cancelEdit();
                  }}
                />
                <button type="button" onClick={confirmEdit} className="rounded-full p-0.5 hover:bg-green-100">
                  <CheckedIcon2 className="h-3 w-3" color="#44C997" />
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div
                key={i}
                className={cn(
                  "flex items-center gap-1 rounded-full border py-1 transition-colors cursor-pointer select-none",
                  selectedIndex === i
                    ? "border-primary/40 bg-primary/5 pl-3 pr-1.5"
                    : "border-border bg-muted/50 px-3 hover:border-border/80 hover:bg-muted",
                )}
                onClick={() => toggleItemSelection(i)}
                role="button"
                aria-pressed={selectedIndex === i}
              >
                <span className="text-xs font-medium">{item}</span>
                {selectedIndex === i && (
                  <>
                    <span className="w-1" />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        startEdit(i);
                      }}
                      className="rounded-full p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
                      title="Modifier"
                    >
                      <PenLineIcon className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeItem(i);
                      }}
                      className="rounded-full p-0.5 text-muted-foreground hover:bg-background hover:text-destructive"
                      title="Supprimer"
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </>
                )}
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
};
